import { Combobox, Dialog, Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  MinusCircleIcon,
  PlusCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAssignSlotMutation,
  useUnassignSlotMutation,
} from '@youshift/shared/hooks/mutations';
import type {
  Section,
  SectionSlot,
  ShiftAssignment,
  User,
} from '@youshift/shared/types';
import {
  dateToString,
  formatUserName,
  areDatesOnTheSameDay,
} from '@youshift/shared/utils';
import { CSSProperties, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';

import { YSButton } from '../Buttons';
import SectionSlotChip from '../Calendars/SectionSlotChip';

interface UnassignShiftProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: User;
  sectionName: string;
  assignmentToDelete: ShiftAssignment;
  assignmentSectionSlot: SectionSlot;
}

export default function UnassignShift({
  open,
  setOpen,
  user,
  sectionName,
  assignmentToDelete,
  assignmentSectionSlot,
}: UnassignShiftProps) {
  const { idItr } = useParams();

  const classNames = (...classes: string[]) =>
    classes.filter(Boolean).join(' ');

  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const unassignSlotMutation = useUnassignSlotMutation(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shiftAssignments', idItr] });
      setOpen(false);
    },
  });

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-center">
                  <div
                    className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10`}
                  >
                    <MinusCircleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      {t('manager.shiftAssignment.eliminateShift')}
                    </Dialog.Title>
                  </div>
                </div>
                {/* Participant */}
                <div className="my-5 flex flex-row items-start gap-6">
                  <h2 className="font-semibold">
                    {t('manager.shiftAssignment.selectParticipant')}
                  </h2>
                  <div className="items-center">
                    <p>
                      {user.firstname} {user.lastname}
                    </p>
                  </div>
                </div>
                {/* Slot */}
                <div className="my-5 flex flex-row items-start gap-6">
                  <h2 className="font-semibold">
                    {t('manager.shiftAssignment.selectSectionSlot')}
                  </h2>
                  <div className="items-center">
                    <span className="block truncate">
                      <SectionSlotChip
                        sectionName={sectionName}
                        start={assignmentSectionSlot.start}
                        end={assignmentSectionSlot.end}
                        small
                      />
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                </div>

                <div className="mt-5 sm:mt-4 flex gap-4">
                  <YSButton
                    type="button"
                    variant="delete"
                    onClick={() => {
                      unassignSlotMutation.mutate({
                        id_itr: Number(idItr),
                        id_shift_assignments: [
                          assignmentToDelete.id_shift_assignment,
                        ],
                      });
                    }}
                  >
                    {t('generic.eliminate')}
                  </YSButton>
                  <YSButton
                    type="button"
                    variant="ghost-primary"
                    onClick={() => setOpen(false)}
                  >
                    {t('generic.cancel')}
                  </YSButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
